import React, { useState, useEffect } from "react";
import { FaBars, FaTimes } from "react-icons/fa";
import { Link } from "react-scroll";
import { motion, AnimatePresence } from "framer-motion";

function NavBar() {
  const [nav, setNav] = useState(false);
  const [darkMode, setDarkMode] = useState(() => {
    // Read saved preference; default to light if not set
    return localStorage.getItem("theme") === "dark";
  });

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add("dark");
      localStorage.setItem("theme", "dark");
    } else {
      document.documentElement.classList.remove("dark");
      localStorage.setItem("theme", "light");
    }
  }, [darkMode]);

  const links = [
    { id: 1, target: "home", label: "Home" },
    { id: 2, target: "about", label: "About" },
    { id: 3, target: "Projects", label: "Projects" },
    { id: 4, target: "experience", label: "Experience" },
    { id: 5, target: "volunteering", label: "Volunteering" }, // new section
    { id: 6, target: "contact", label: "Contact" },
  ];

  return (
    <nav
      className="flex justify-between items-center w-full h-16 px-4 sm:px-8 fixed z-50 transition-colors
                 bg-gradient-to-r from-gray-50 to-gray-200 text-gray-900
                 dark:from-gray-900 dark:to-gray-800 dark:text-white"
    >
      {/* Text Logo */}
      <motion.div
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        className="flex items-center"
      >
        <Link
          to="home"
          smooth={true}
          duration={500}
          className="flex items-center cursor-pointer"
        >
          <span className="text-xl font-bold bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
            YASEN
          </span>
        </Link>
      </motion.div>

      {/* Desktop Links */}
      <ul className="hidden md:flex space-x-6 lg:space-x-8">
        {links.map(({ id, target, label }) => (
          <motion.li key={id} whileHover={{ scale: 1.05 }} className="relative">
            <Link
              to={target}
              smooth
              duration={500}
              activeClass="active"
              className="group px-3 py-2 cursor-pointer font-medium
                         hover:text-cyan-600 dark:hover:text-blue-500 transition-colors"
            >
              {label}
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-cyan-600 dark:bg-blue-500 transition-all duration-300 group-hover:w-full"></span>
            </Link>
          </motion.li>
        ))}
      </ul>

      {/* Dark/Light Mode Toggle - shows target mode icon (fix inversion) */}
      <button
        onClick={() => setDarkMode((prev) => !prev)}
        className="mx-3 relative flex items-center justify-center w-12 h-7 rounded-full
                   border-2 border-gray-300 bg-gray-100
                   dark:border-gray-600 dark:bg-gray-800
                   transition-colors duration-300 focus:outline-none"
        aria-label={darkMode ? "Switch to light mode" : "Switch to dark mode"}
        title={darkMode ? "Switch to light mode" : "Switch to dark mode"}
      >
        <span
          className="absolute left-1 top-1 flex items-center justify-center w-5 h-5 transition-transform duration-300
                     bg-white dark:bg-gray-900 shadow rounded-full"
          style={{ transform: darkMode ? "translateX(20px)" : "translateX(0px)" }}
        >
          {/* Show target mode icon: if currently dark -> show Sun (light target), else Moon */}
          {darkMode ? (
            // Sun icon (target: light)
            <svg width="16" height="16" fill="currentColor" className="text-yellow-500" viewBox="0 0 20 20">
              <path d="M10 3a1 1 0 011 1v1a1 1 0 01-2 0V4a1 1 0 011-1zm7 7a1 1 0 01-1 1h-1a1 1 0 110-2h1a1 1 0 011 1zm-7 7a1 1 0 011-1v-1a1 1 0 10-2 0v1a1 1 0 011 1zm-7-7a1 1 0 011-1H4a1 1 0 100 2h1a1 1 0 01-1-1zm11.071-6.071a1 1 0 011.415 0l.707.707a1 1 0 01-1.414 1.415l-.707-.707a1 1 0 010-1.415zm-8.486 0a1 1 0 010 1.415l-.707.707a1 1 0 01-1.415-1.414l.707-.707a1 1 0 011.415 0zm8.486 12.142a1 1 0 010 1.415l-.707.707a1 1 0 01-1.415-1.414l.707-.707a1 1 0 011.415 0zm-8.486 0a1 1 0 01-1.415 0l-.707.707a1 1 0 001.414 1.415l.707-.707a1 1 0 010-1.415zM10 7a3 3 0 100 6 3 3 0 000-6z" />
            </svg>
          ) : (
            // Moon icon (target: dark)
            <svg width="16" height="16" fill="currentColor" className="text-blue-700" viewBox="0 0 20 20">
              <path d="M17.293 13.293a8 8 0 01-10.586-10.586A8 8 0 1017.293 13.293z" />
            </svg>
          )}
        </span>
      </button>

      {/* Mobile Hamburger */}
      <div
        onClick={() => setNav(!nav)}
        className="cursor-pointer z-50 text-gray-900 dark:text-white md:hidden p-2"
        aria-label="Menu"
      >
        {nav ? <FaTimes size={24} /> : <FaBars size={24} />}
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {nav && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="md:hidden fixed inset-0 z-40 pt-16 backdrop-blur-sm transition-colors
                       bg-white/95 dark:bg-gray-900/95"
          >
            <motion.ul
              initial={{ y: -20 }}
              animate={{ y: 0 }}
              className="px-2 pt-8 pb-3 space-y-4"
            >
              {links.map(({ id, target, label }) => (
                <motion.li
                  key={id}
                  whileHover={{ x: 5 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-3 py-2 rounded-md text-center"
                >
                  <Link
                    onClick={() => setNav(false)}
                    to={target}
                    smooth
                    duration={500}
                    className="block text-2xl text-gray-700 dark:text-gray-300 hover:text-cyan-600 dark:hover:text-blue-500 py-4 transition-colors"
                  >
                    {label}
                  </Link>
                </motion.li>
              ))}
            </motion.ul>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}

export default NavBar;